<template lang="pug">
  <div>
    .w-100.mh-100.h-100(v-if="reports")
      template(v-for="report in reports")
        div.mb-3(v-if="report.data?.length")
          .h6 {{ report.name }}
          dx-data-grid(ref="grid"
          :data-source='report.data'
          :selection="{ mode: 'single' }"
          :show-borders='true'
          :style="{ 'max-height': '300px', width: 'auto' }"
          :columns="!reportData.IsSummary ? report.fields.filter(f => f.dataField !== 'ObjectName') : report.fields"
          :row-alternation-enabled="true"
          :allow-column-reordering="true"
          :allow-column-dragging="false"
          :summary="report.summary"
          @content-ready="onContentReady"
          @cell-click="onSelectionChanged")
            dx-scrolling(mode='virtual', :useNative="false")
            dx-sorting(mode="multiple")
            dx-load-panel(:enabled="true")
            dx-grouping(:auto-expand-all="reportData.IsSummary")
        .h6.mb-3(v-else) {{ report.name }}: нет данных
    .h6.mb-3(v-else) Данные отсутствуют
  </div>
</template>
<script>
import { DxDataGrid, DxScrolling, DxPaging, DxHeaderFilter, DxSorting, DxLoadPanel, DxGrouping } from 'devextreme-vue/data-grid';
import ruMessages from 'devextreme/localization/messages/ru.json';
import { locale, loadMessages } from 'devextreme/localization';
import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.compact.css';
import dataGridReports from '@/mixins/dataGridReports';
import sizeProps from '@/mixins/sizeProps';
import D3 from '~/components/VueD3';
import { getDateString } from '~/helpers';
import { SETTINGS_EVENTS } from '~/consts/eventTypes';

function formatFloat (params) {
  return params.value?.toFixed?.(1) || params.value;
}

const objectNameField = {
  dataField: 'ObjectName',
  caption: 'Объект',
  groupIndex: 0,
};

const ridesFields = [
  objectNameField,
  {
    dataField: 'Begin',
    caption: 'Начало',
    alignment: 'center',
    customizeText: params => getDateString(params.value * 1000),
    width: 120,
  }, {
    dataField: 'BeginLocation',
    caption: 'Начальное положение',
  }, {
    dataField: 'End',
    caption: 'Конец',
    alignment: 'center',
    customizeText: params => getDateString(params.value * 1000),
    width: 120,
  }, {
    dataField: 'EndLocation',
    caption: 'Конечное положение',
  }, {
    dataField: 'Duration',
    caption: 'Длительность',
    alignment: 'center',
    width: 90,
  }, {
    dataField: 'Mileage',
    caption: 'Пробег, км',
    customizeText: formatFloat,
    alignment: 'right',
    width: 75,
  }, {
    dataField: 'AvgSpeed',
    caption: 'Сред. скорость, км/ч',
    alignment: 'right',
    customizeText: formatFloat,
    width: 130,
  }, {
    dataField: 'MaxSpeed',
    caption: 'Макс. скорость, км/ч',
    alignment: 'right',
    width: 130,
  }, {
    dataField: 'FuelSensorSum',
    caption: 'Потрачено по ДУТ, л',
    customizeText: formatFloat,
    alignment: 'right',
    width: 130,
  }, {
    dataField: 'FuelSensorAvg',
    caption: 'Сред. расход по ДУТ, л/100 км',
    alignment: 'right',
    customizeText: formatFloat,
    width: 180,
  }, {
    dataField: 'BeginFuelLevel',
    caption: 'Начальный уровень топлива, л',
    alignment: 'right',
    customizeText: formatFloat,
    width: 190,
  }, {
    dataField: 'EndFuelLevel',
    caption: 'Конечный уровень топлива, л',
    alignment: 'right',
    customizeText: formatFloat,
    width: 185,
  },
];

const commonLocationField = {
  dataField: 'Location',
  caption: 'Местоположение',
};

const tankerFillsFields = [
  objectNameField,
  {
    dataField: 'SensorName',
    caption: 'Датчик',
    alignment: 'right',
    width: 110,
  }, {
    dataField: 'Begin',
    caption: 'Вкл.',
    alignment: 'center',
    customizeText: params => getDateString(params.value * 1000),
    width: 120,
  }, {
    dataField: 'End',
    caption: 'Откл.',
    alignment: 'center',
    customizeText: params => getDateString(params.value * 1000),
    width: 120,
  }, {
    dataField: 'Duration',
    caption: 'Длительность',
    alignment: 'center',
    width: 90,
  },
  commonLocationField,
  {
    dataField: 'Volume',
    caption: 'Заправлено топлива, л',
    customizeText: formatFloat,
    alignment: 'right',
    width: 145,
  }, {
    dataField: 'TransportName',
    caption: 'Водитель',
    alignment: 'right',
    width: 100,
  },
];

const stopsAndParkingsFields = [
  objectNameField,
  {
    dataField: 'Begin',
    caption: 'Начало',
    alignment: 'center',
    customizeText: params => getDateString(params.value * 1000),
    width: 120,
  }, {
    dataField: 'End',
    caption: 'Конец',
    alignment: 'center',
    customizeText: params => getDateString(params.value * 1000),
    width: 120,
  }, {
    dataField: 'Duration',
    caption: 'Длительность',
    alignment: 'center',
    width: 90,
  },
  commonLocationField,
  {
    dataField: 'LocationLatitude',
    caption: 'Широта',
    width: 80,
  }, {
    dataField: 'LocationLongitude',
    caption: 'Долгота',
    width: 80,
  },
];

const fillsFields = [
  objectNameField,
  {
    dataField: 'Begin',
    caption: 'Время',
    alignment: 'center',
    customizeText: params => getDateString(params.value * 1000),
    width: 120,
  },
  commonLocationField,
  {
    dataField: 'BeginFuelLevel',
    caption: 'Начальный уровень топлива, л',
    customizeText: formatFloat,
    alignment: 'right',
    width: 190,
  }, {
    dataField: 'EndFuelLevel',
    caption: 'Конечный уровень топлива, л',
    customizeText: formatFloat,
    alignment: 'right',
    width: 185,
  }, {
    dataField: 'Volume',
    caption: 'Заправлено топлива, л',
    customizeText: formatFloat,
    alignment: 'right',
    width: 145,
  }, {
    dataField: 'SensorName',
    caption: 'Имя датчика',
    alignment: 'right',
    width: 110,
  },
];

const commonTotalsFields = [
  objectNameField,
  {
    dataField: 'RidesMileage',
    caption: 'Пробег в поездках, км',
    customizeText: formatFloat,
    alignment: 'right',
    width: 150,
  }, {
    dataField: 'AvgSpeed',
    caption: 'Средняя скорость, км/ч',
    customizeText: formatFloat,
    alignment: 'right',
    width: 150,
  }, {
    dataField: 'RidesTime',
    caption: 'Время в движении',
    alignment: 'center',
    width: 130,
  }, {
    dataField: 'EngineHours',
    caption: 'Моточасы',
    alignment: 'center',
    width: 90,
  }, {
    dataField: 'StopsTime',
    caption: 'Стоянки',
    alignment: 'center',
    width: 90,
  }, {
    dataField: 'BeginFuelLevel',
    caption: 'Начальный уровень топлива, л',
    customizeText: formatFloat,
    alignment: 'right',
    width: 190,
  }, {
    dataField: 'EndFuelLevel',
    caption: 'Конечный уровень топлива, л',
    customizeText: formatFloat,
    alignment: 'right',
    width: 185,
  }, {
    dataField: 'Volume',
    caption: 'Заправлено топлива, л',
    customizeText: formatFloat,
    alignment: 'right',
    width: 145,
  },
];

const mainTotalsFields = [...commonTotalsFields];
mainTotalsFields.splice(6, 0, {
  dataField: 'FuelSensorSum',
  caption: 'Потрачено по ДУТ, л',
  customizeText: formatFloat,
  alignment: 'right',
  width: 130,
});

const tankTotalsFields = [...commonTotalsFields];
tankTotalsFields.splice(3, 0, {
  dataField: 'MaxSpeed',
  caption: 'Максимальная скорость, км/ч',
  alignment: 'right',
  width: 180,
});

export default {
  name: 'ChosenDataReport',
  components: {
    D3,
    DxDataGrid,
    DxScrolling,
    DxPaging,
    DxHeaderFilter,
    DxSorting,
    DxLoadPanel,
    DxGrouping,
  },
  mixins: [dataGridReports, sizeProps],
  props: {
    reportData: {
      type: Object,
      default () {
        return {};
      },
    },
    track: {
      type: Object,
      default () {
        return {};
      },
    },
    colors: {
      type: Object,
      default () {
        return {};
      },
    },
  },
  data () {
    return {
      rowClick: null,
    };
  },
  computed: {
    reports () {
      if (this.reportData?.IsEmptyResult) {
        return null;
      }

      const data = this.reportData?.Data?.Data;
      return [{
        name: 'Поездки',
        data: data?.Rides,
        fields: ridesFields,
        summary: this.getSummary({
          Duration: data?.RidesSumDuration,
          Mileage: data?.RidesSumMileage?.toFixed?.(1),
          AvgSpeed: data?.RidesAvgSpeed?.toFixed?.(1),
          MaxSpeed: data?.RidesMaxSpeed,
          FuelSensorSum: data?.RidesSumFuelSensorSum?.toFixed?.(1),
          FuelSensorAvg: data?.RidesAvgFuelSensorAvg?.toFixed?.(1),
          BeginFuelLevel: data?.RidesBeginFuelLevel?.toFixed?.(1),
          EndFuelLevel: data?.RidesEndFuelLevel?.toFixed?.(1),
        }, this.getSummaryFields(['Duration', 'Mileage', 'AvgSpeed', 'MaxSpeed', 'FuelSensorSum', 'FuelSensorAvg', 'BeginFuelLevel', 'EndFuelLevel'])),
      }, {
        name: 'Остановки',
        data: data?.Stops,
        fields: stopsAndParkingsFields,
        summary: this.getSummary({
          Duration: data?.StopsSumDuration,
        }, this.getSummaryFields(['Duration'])),
      }, {
        name: 'Стоянки',
        data: data?.Parkings,
        fields: stopsAndParkingsFields,
        summary: this.getSummary({
          Duration: data?.ParkingsSumDuration,
        }, this.getSummaryFields(['Duration'])),
      }, {
        name: 'Выдача по счетчику',
        data: data?.TankerFills,
        fields: tankerFillsFields,
        summary: this.getSummary({
          Duration: data?.TankerFillsSumDuration,
          Volume: data?.TankerFillsSumVolume?.toFixed?.(1),
        }, this.getSummaryFields(['Duration', 'Volume'])),
      }, {
        name: 'Заправки (БАК)',
        data: data?.MainFills,
        fields: fillsFields,
        summary: this.getSummary({
          Volume: data?.MainFillsSumVolume?.toFixed?.(1),
        }, this.getSummaryFields(['Volume'])),
      }, {
        name: 'Заправки (Цистерна)',
        data: data?.TankFills,
        fields: fillsFields,
        summary: this.getSummary({
          Volume: data?.TankFillsSumVolume?.toFixed?.(1),
        }, this.getSummaryFields(['Volume'])),
      }, {
        name: 'Сводка (БАК)',
        data: data?.MainTotals,
        fields: mainTotalsFields,
      }, {
        name: 'Сводка (Цистерна)',
        data: data?.TankTotals,
        fields: tankTotalsFields,
      }];
    },
  },
  watch: {
    reportData (value) {
      this.rowClick = null;
      // if (value.IsSummary) {
      //   this.convertSummaryData(value);
      // }
      this.getAddresses();
    },
  },
  created () {
    loadMessages(ruMessages);
    locale('ru');
  },
  mounted () {
    // if (this.reportData.IsSummary) {
    //   this.convertSummaryData(this.reportData);
    // }
    this.getAddresses();
    this.$eventBus.subscribe(
      SETTINGS_EVENTS.addressSettingsChanged,
      // eslint-disable-next-line no-underscore-dangle
      this._name,
      this.getAddresses,
    );
  },
  beforeDestroy () {
    this.$eventBus.unsubscribe(
      SETTINGS_EVENTS.addressSettingsChanged,
      // eslint-disable-next-line no-underscore-dangle
      this._name,
    );
  },
  methods: {
    onSelectionChanged (item) {
      if (item.rowType === 'data') {
        if (this.reportData.BuildTrack && this.track.Id) {
          if (!(this.rowClick && this.rowClick.Begin === item.data.Begin && this.rowClick.End === item.data.End)) {
            const timeZone = Number(this.reportData.TimeZone.split(':', 3)[0]);
            const periods = [[new Date((item.data.Begin - timeZone * 3600) * 1000), new Date((item.data.End - timeZone * 3600) * 1000)]];
            this.$store.dispatch('tracks/updateTrack', { reportId: this.track.Id, secondColor: this.colors.alternative.hex, periods });
            this.reportData.periodsPartTrack = periods;
            this.rowClick = {
              Begin: item.data.Begin,
              End: item.data.End,
            };
          }
        }
        if (item.column.dataField === 'Begin' || item.column.dataField === 'End' || item.column.dataField === 'Location') {
          const value = {
            lat: item.data.LocationLatitude,
            lng: item.data.LocationLongitude,
          };
          this.$store.commit('map/setCenter', value);
          this.$store.commit('tracks/setActivePoint', [value.lat, value.lng]);
        }
      }
    },
    getSummary (data, fields) {
      return {
        calculateCustomSummary: (options) => {
          if (options.summaryProcess === 'finalize' && options.name in data) {
            const value = options;
            value.totalValue = data[options.name];
          }
        },
        totalItems: fields,
      };
    },
    getSummaryFields (fields) {
      return fields.map(field => ({
        column: field,
        name: field,
        summaryType: 'custom',
        displayFormat: '{0}',
      }));
    },
    convertSummaryData (data) {
      const rows = [];
      const totals = [];
      data.Data.Data.forEach((items) => {
        items.Rows?.forEach((_row, i) => {
          const row = _row;
          row.RowNum = i + 1;
          row.ObjectName = items.ObjectName;
          rows.push(row);
        });
        items.Totals?.forEach((_row, i) => {
          const row = _row;
          row.ObjectName = items.ObjectName;
          totals.push(row);
        });
      });
      this.reportSummary.Rows = rows;
      this.reportSummary.Totals = totals;
    },
    getAddressesForSingleObject (data) {
      data?.Rides?.forEach((row) => {
        this.getAddress(row.BeginLocationLatitude, row.BeginLocationLongitude, row, 'BeginLocation');
        this.getAddress(row.EndLocationLatitude, row.EndLocationLongitude, row, 'EndLocation');
      });
      data?.Stops?.forEach((row) => {
        this.getAddress(row.LocationLatitude, row.LocationLongitude, row);
      });
      data?.Parkings?.forEach((row) => {
        this.getAddress(row.LocationLatitude, row.LocationLongitude, row);
      });
      data?.TankerFills?.forEach((row) => {
        this.getAddress(row.LocationLatitude, row.LocationLongitude, row);
      });
      data?.MainFills?.forEach((row) => {
        this.getAddress(row.LocationLatitude, row.LocationLongitude, row);
      });
      data?.TankFills?.forEach((row) => {
        this.getAddress(row.LocationLatitude, row.LocationLongitude, row);
      });
    },
    getAddresses () {
      this.getAddressesForSingleObject(this.reportData?.Data?.Data);
    },
    async getAddress (lat, lon, _row, param = 'Location') {
      // eslint-disable-next-line no-param-reassign
      _row[param] = null;
      // eslint-disable-next-line no-param-reassign
      _row[param] = await this.$store.dispatch('nominatim/getAddress', { lat, lon });
    },
  },
};
</script>
<style lang="stylus" scoped>

</style>
